import { useSession } from 'next-auth/react';
import { useQuery } from 'react-query';
import { QueryId, STALE_TIME } from '@/integrations/content/utils';
import formatUrlSearchParameters from '@/libraries/formatters/format-url-search-parameters';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

type UseQueryOnboardingFlowStepHeaderData =
  Content.Components['OnboardingFlowStepHeaderModuleDto'];

interface UseQueryOnboardingFlowStepHeaderOptions {
  initialData?: UseQueryOnboardingFlowStepHeaderData;
  parameters: {
    id: string;
    pageId: string;
  };
}

const useQueryOnboardingFlowStepHeader = ({
  initialData,
  parameters,
}: UseQueryOnboardingFlowStepHeaderOptions) => {
  const locale = getLocale();
  const { data: session } = useSession();

  const query = useQuery<UseQueryOnboardingFlowStepHeaderData>(
    [
      QueryId.ONBOARDING_FLOW_STEP_HEADER,
      parameters,
      locale,
      session?.user.accessToken,
    ],
    async () => {
      const baseUrl = '/api/content-modules/onboarding-flow-step-header';
      const queryParameters = formatUrlSearchParameters(parameters);

      const fetchResponse = await fetch(
        `${baseUrl}?${queryParameters.toString()}`,
        {
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
            ...(session?.user.accessToken
              ? { ['Authorization']: `Bearer ${session?.user.accessToken}` }
              : {}),
          },
          method: 'GET',
        },
      )
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error('hooks.useQueryOnboardingFlowStepHeader', error);

          throw error;
        });

      return fetchResponse;
    },
    {
      enabled:
        !!parameters.id && !!parameters.pageId && !!session?.user.accessToken,
      initialData,
      keepPreviousData: true,
      staleTime: STALE_TIME,
    },
  );

  return query;
};

export default useQueryOnboardingFlowStepHeader;
